import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, DollarSign, TrendingUp, TrendingDown, Building, Download, AlertTriangle } from 'lucide-react';
import { Badge, Select, Button } from '@/components/ui';
import { GlassLoadingSpinner, CollapsibleMetricsSection } from '@/design-system';
import { apiCall } from '@/utils/api';
import { formatCurrencyCompact, formatPercentTrend } from '@/utils/formatters';
// Design System Imports
import { MetricCard } from '@/design-system/components/data-display/MetricCard';
import { GlassPageHeader } from '@/design-system/components/layout/GlassPageHeader';

interface Team {
  teamId: string;
  teamName: string;
  memberCount: number;
  costProfile: {
    id: string;
    name: string;
    hourlyRate: number;
  };
  monthlyCost: number;
  annualCost: number;
}

interface ISBudget {
  isId: string;
  isName: string;
  isCategory: string;
  epics: Array<{
    epicId: string;
    epicName: string;
    priority: string;
    requestedTeamWeeks: number;
    budget: number;
  }>;
  totalBudget: number;
}

interface ProgramFinancials {
  programId: string;
  programName: string;
  programCategory: string;
  leadId?: string;
  leadName?: string;
  teamCount: number;
  teams: Team[];
  operationalCost: {
    monthly: number;
    annual: number;
  };
  isBudget: {
    total: number;
    monthly: number;
    annual: number;
    byCategory: {
      BUSINESS: number;
      TECH: number;
      SUPPORT: number;
    };
    investibleStructures: ISBudget[];
  };
  variance: {
    monthly: number;
    annual: number;
    percentage: number;
    isOverBudget: boolean;
    isUnderBudget: boolean;
  };
}

interface Summary {
  totalPrograms: number;
  totalOperationalCost: {
    monthly: number;
    annual: number;
  };
  totalISBudget: {
    monthly: number;
    annual: number;
  };
  totalVariance: {
    monthly: number;
    annual: number;
    percentage: number;
  };
  overBudgetPrograms: number;
  underBudgetPrograms: number;
  categoryBreakdown: {
    BUSINESS: number;
    TECH: number;
    SUPPORT: number;
  };
}

interface FinanceData {
  planningPeriod: {
    id: string;
    name: string;
  };
  programs: ProgramFinancials[];
  summary: Summary;
}

const FinanceOperationalCostPage: React.FC = () => {
  const [data, setData] = useState<FinanceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPrograms, setExpandedPrograms] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'monthly' | 'annual'>('annual');
  const [selectedPlanningPeriodId, setSelectedPlanningPeriodId] = useState<string>('');
  const [programCategory, setProgramCategory] = useState<string>('');
  const [isCategory, setIsCategory] = useState<string>('');
  const [showOnlyOverBudget, setShowOnlyOverBudget] = useState(false);
  const [planningPeriods, setPlanningPeriods] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    fetchPlanningPeriods();
  }, []);

  useEffect(() => {
    fetchFinanceData();
  }, [selectedPlanningPeriodId, programCategory, isCategory]);

  const fetchPlanningPeriods = async () => {
    try {
      const response = await apiCall('GET', '/planning-periods');
      const periods = response.data?.data || response.data || [];
      const periodsArray = Array.isArray(periods) ? periods : [];
      setPlanningPeriods(periodsArray);

      if (periodsArray.length > 0 && !selectedPlanningPeriodId) {
        setSelectedPlanningPeriodId(periodsArray[0].id);
      }
    } catch (err) {
      console.error('Error fetching planning periods:', err);
      setPlanningPeriods([]);
    }
  };

  const fetchFinanceData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (selectedPlanningPeriodId) params.append('planningPeriodId', selectedPlanningPeriodId);
      if (programCategory) params.append('programCategory', programCategory);
      if (isCategory) params.append('isCategory', isCategory);

      const response = await apiCall('GET', `/finance/operational-cost?${params}`);
      const financeData = response.data?.data || response.data;
      setData(financeData);
    } catch (err: any) {
      console.error('Error fetching finance data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load finance data');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleProgram = (programId: string) => {
    const newExpanded = new Set(expandedPrograms);
    if (newExpanded.has(programId)) {
      newExpanded.delete(programId);
    } else {
      newExpanded.add(programId);
    }
    setExpandedPrograms(newExpanded);
  };


  const getVarianceBadge = (variance: { isOverBudget: boolean; isUnderBudget: boolean; percentage: number }) => {
    if (variance.isOverBudget) {
      return <Badge variant="danger">Over Budget ({formatPercentTrend(variance.percentage)})</Badge>;
    }
    if (variance.isUnderBudget) {
      return <Badge variant="success">Under Budget ({formatPercentTrend(variance.percentage)})</Badge>;
    }
    return <Badge variant="default">On Budget</Badge>;
  };

  const getCategoryBadge = (category: string) => {
    switch (category) {
      case 'BUSINESS':
        return <Badge variant="primary">Business</Badge>;
      case 'TECH':
        return <Badge variant="info">Tech</Badge>;
      case 'SUPPORT':
        return <Badge variant="secondary">Support</Badge>;
      default:
        return <Badge variant="default">{category}</Badge>;
    }
  };

  const handleExport = () => {
    if (!data) return;

    // Prepare CSV data
    const csvRows = [];
    csvRows.push(['Finance Operational Cost Report']);
    csvRows.push(['Planning Period:', data.planningPeriod.name]);
    csvRows.push(['View Mode:', viewMode === 'monthly' ? 'Monthly' : 'Annual']);
    csvRows.push([]);

    // Summary section
    csvRows.push(['SUMMARY']);
    const opCost = viewMode === 'monthly' ? data.summary.totalOperationalCost.monthly : data.summary.totalOperationalCost.annual;
    const isBudget = viewMode === 'monthly' ? data.summary.totalISBudget.monthly : data.summary.totalISBudget.annual;
    const variance = viewMode === 'monthly' ? data.summary.totalVariance.monthly : data.summary.totalVariance.annual;

    csvRows.push(['Total Operational Cost', opCost.toFixed(2)]);
    csvRows.push(['Total IS Budget', isBudget.toFixed(2)]);
    csvRows.push(['Variance', variance.toFixed(2)]);
    csvRows.push(['Variance %', data.summary.totalVariance.percentage.toFixed(2) + '%']);
    csvRows.push([]);

    // Programs section
    csvRows.push(['PROGRAMS']);
    csvRows.push(['Program', 'Category', 'Teams', 'Operational Cost', 'IS Budget', 'Variance', 'Variance %', 'Status']);

    data.programs.forEach(program => {
      const progOpCost = viewMode === 'monthly' ? program.operationalCost.monthly : program.operationalCost.annual;
      const progISBudget = viewMode === 'monthly' ? program.isBudget.monthly : program.isBudget.annual;
      const progVariance = viewMode === 'monthly' ? program.variance.monthly : program.variance.annual;

      csvRows.push([
        program.programName,
        program.programCategory,
        program.teamCount.toString(),
        progOpCost.toFixed(2),
        progISBudget.toFixed(2),
        progVariance.toFixed(2),
        program.variance.percentage.toFixed(2) + '%',
        program.variance.isOverBudget ? 'Over Budget' : program.variance.isUnderBudget ? 'Under Budget' : 'On Budget',
      ]);
    });

    // Convert to CSV string
    const csvContent = csvRows.map(row => row.join(',')).join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `finance-operational-cost-${viewMode}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <GlassLoadingSpinner size="lg" variant="gradient" label="Loading cost data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-status-error/10 border border-status-error/30 rounded-lg">
        <p className="text-status-error">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-gray-600">No finance data available</p>
      </div>
    );
  }

  const filteredPrograms = showOnlyOverBudget
    ? data.programs.filter(p => p.variance.isOverBudget)
    : data.programs;

  const displayOpCost = viewMode === 'monthly' ? data.summary.totalOperationalCost.monthly : data.summary.totalOperationalCost.annual;
  const displayISBudget = viewMode === 'monthly' ? data.summary.totalISBudget.monthly : data.summary.totalISBudget.annual;
  const displayVariance = viewMode === 'monthly' ? data.summary.totalVariance.monthly : data.summary.totalVariance.annual;

  return (
    <div className="glass-page-bg min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <GlassPageHeader
          title="Finance Operational Cost"
          subtitle={`Planning Period: ${data?.planningPeriod?.name || 'All Periods'}`}
          description="Consolidated view of program operational costs and IS budget allocations"
          icon={<DollarSign className="w-6 h-6" />}
          iconGradient="rose"
          metadata={[
            { icon: <Building className="w-4 h-4" />, label: 'Programs', value: String(data?.summary?.totalPrograms || 0) },
            { icon: <AlertTriangle className="w-4 h-4" />, label: 'Over Budget', value: String(data?.summary?.overBudgetPrograms || 0) },
          ]}
          actions={
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              leftIcon={<Download className="w-4 h-4" />}
            >
              Export to CSV
            </Button>
          }
          className="mb-6 section-reveal"
        />

        {/* Summary Metrics */}
        <CollapsibleMetricsSection storageKey="operational-costs">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 section-reveal" style={{ animationDelay: '100ms' }}>
            <MetricCard
              title="Operational Cost"
              value={formatCurrencyCompact(displayOpCost)}
              subtitle={viewMode === 'monthly' ? 'per month' : 'annual'}
              icon={<Building className="w-5 h-5" />}
              iconColor="amber"
            />
            <MetricCard
              title="IS Budget"
              value={formatCurrencyCompact(displayISBudget)}
              subtitle="allocated from ISs"
              icon={<DollarSign className="w-5 h-5" />}
              iconColor="violet"
            />
            <MetricCard
              title="Variance"
              value={formatCurrencyCompact(Math.abs(displayVariance))}
              subtitle={displayVariance >= 0 ? 'under budget' : 'over budget'}
              icon={displayVariance >= 0 ? <TrendingDown className="w-5 h-5" /> : <TrendingUp className="w-5 h-5" />}
              iconColor={displayVariance >= 0 ? 'emerald' : 'rose'}
              trend={{
                value: Math.abs(data.summary.totalVariance.percentage),
                direction: displayVariance >= 0 ? 'down' : 'up'
              }}
            />
            <MetricCard
              title="Over Budget"
              value={String(data.summary.overBudgetPrograms)}
              subtitle={`of ${data.summary.totalPrograms} programs`}
              icon={<AlertTriangle className="w-5 h-5" />}
              iconColor={data.summary.overBudgetPrograms > 0 ? 'rose' : 'emerald'}
            />
          </div>
        </CollapsibleMetricsSection>

        {/* Filters and View Toggle */}
        <div className="glass rounded-xl p-4 section-reveal" style={{ animationDelay: '200ms' }}>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
                Planning Period
              </label>
              <Select
                value={selectedPlanningPeriodId}
                onChange={(e) => setSelectedPlanningPeriodId(e.target.value)}
                options={Array.isArray(planningPeriods) ? planningPeriods.map(p => ({ value: p.id, label: p.name })) : []}
              />
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
                Program Category
              </label>
              <Select
                value={programCategory}
                onChange={(e) => setProgramCategory(e.target.value)}
                options={[
                  { value: '', label: 'All Categories' },
                  { value: 'BUSINESS', label: 'Business' },
                  { value: 'TECH', label: 'Tech' },
                  { value: 'SUPPORT', label: 'Support' },
                ]}
              />
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
                IS Category
              </label>
              <Select
                value={isCategory}
                onChange={(e) => setIsCategory(e.target.value)}
                options={[
                  { value: '', label: 'All IS Categories' },
                  { value: 'BUSINESS', label: 'Business' },
                  { value: 'TECH', label: 'Tech' },
                  { value: 'SUPPORT', label: 'Support' },
                ]}
              />
            </div>

            <div className="flex items-center gap-2 mt-6">
              <div className="flex gap-2">
                <Button
                  variant={viewMode === 'monthly' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('monthly')}
                >
                  Monthly
                </Button>
                <Button
                  variant={viewMode === 'annual' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('annual')}
                >
                  Annual
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOnlyOverBudget}
                  onChange={(e) => setShowOnlyOverBudget(e.target.checked)}
                  className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm font-medium text-gray-700">Show only over budget</span>
              </label>
            </div>
          </div>
        </div>

        {/* Programs List */}
        <div className="space-y-4 section-reveal" style={{ animationDelay: '300ms' }}>
          {filteredPrograms.map((program, index) => {
            const progOpCost = viewMode === 'monthly' ? program.operationalCost.monthly : program.operationalCost.annual;
            const progISBudget = viewMode === 'monthly' ? program.isBudget.monthly : program.isBudget.annual;
            const progVariance = viewMode === 'monthly' ? program.variance.monthly : program.variance.annual;

            return (
              <div key={program.programId} className="glass-strong rounded-xl overflow-hidden hover-lift" style={{ animationDelay: `${350 + index * 50}ms` }}>
                <div
                  className={`p-5 cursor-pointer transition-all duration-200 ${
                    program.variance.isOverBudget
                      ? 'bg-gradient-to-r from-rose-50/80 via-red-50/60 to-transparent dark:from-rose-900/30 dark:via-red-900/20 dark:to-transparent border-l-4 border-rose-500'
                      : program.variance.isUnderBudget
                      ? 'bg-gradient-to-r from-emerald-50/80 via-green-50/60 to-transparent dark:from-emerald-900/30 dark:via-green-900/20 dark:to-transparent border-l-4 border-emerald-500'
                      : 'bg-gradient-to-r from-violet-50/60 to-transparent dark:from-violet-900/30 dark:to-transparent border-l-4 border-violet-400'
                  }`}
                  onClick={() => toggleProgram(program.programId)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      {expandedPrograms.has(program.programId) ? (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      )}
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{program.programName}</h3>
                        {program.leadName && (
                          <p className="text-xs text-gray-500 mt-0.5">Lead: {program.leadName}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Teams</p>
                        <p className="text-base font-bold text-gray-900 mt-0.5">{program.teamCount}</p>
                      </div>

                      <div className="text-right">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Operational Cost</p>
                        <p className="text-base font-bold text-amber-600 dark:text-amber-400 mt-0.5">
                          {formatCurrencyCompact(progOpCost)}
                        </p>
                      </div>

                      <div className="text-right">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">IS Budget</p>
                        <p className="text-base font-bold text-violet-600 dark:text-violet-400 mt-0.5">
                          {formatCurrencyCompact(progISBudget)}
                        </p>
                      </div>

                      <div className="text-right min-w-[120px]">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Variance</p>
                        <div className="flex items-center gap-2 justify-end mt-0.5">
                          <p className={`text-base font-bold ${
                            program.variance.isOverBudget ? 'text-rose-600 dark:text-rose-400' :
                            program.variance.isUnderBudget ? 'text-emerald-600 dark:text-emerald-400' :
                            'text-gray-600 dark:text-gray-400'
                          }`}>
                            {formatCurrencyCompact(Math.abs(progVariance))}
                          </p>
                          {program.variance.isOverBudget ? (
                            <TrendingUp className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                          ) : program.variance.isUnderBudget ? (
                            <TrendingDown className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                          ) : null}
                        </div>
                      </div>

                      {getVarianceBadge(program.variance)}
                    </div>
                  </div>
                </div>

                {expandedPrograms.has(program.programId) && (
                  <div className="p-5 bg-white/40 backdrop-blur-sm border-t border-white/20">
                    {/* Teams Breakdown */}
                    <div className="mb-5">
                      <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">
                        Teams ({program.teams.length})
                      </h4>
                      <div className="space-y-2">
                        {program.teams.map(team => {
                          const teamCost = viewMode === 'monthly' ? team.monthlyCost : team.annualCost;
                          return (
                            <div key={team.teamId} className="glass rounded-lg p-3 hover:bg-white/60 transition-colors">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm font-bold text-gray-900">{team.teamName}</p>
                                  <p className="text-xs text-gray-600 mt-0.5">
                                    {team.memberCount} members • {team.costProfile.name} (${team.costProfile.hourlyRate}/hr)
                                  </p>
                                </div>
                                <p className="text-sm font-bold text-amber-600">
                                  {formatCurrencyCompact(teamCost)}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* IS Category Breakdown */}
                    <div className="mb-5">
                      <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">
                        IS Budget by Category
                      </h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="glass-strong rounded-lg p-4 bg-gradient-to-br from-blue-50/80 to-blue-100/40 dark:from-blue-900/30 dark:to-blue-800/20 hover-lift">
                          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">Business</p>
                          <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                            {formatCurrencyCompact(program.isBudget.byCategory.BUSINESS)}
                          </p>
                        </div>
                        <div className="glass-strong rounded-lg p-4 bg-gradient-to-br from-sky-50/80 to-blue-100/40 dark:from-sky-900/30 dark:to-blue-800/20 hover-lift">
                          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">Tech</p>
                          <p className="text-xl font-bold text-violet-600 dark:text-violet-400">
                            {formatCurrencyCompact(program.isBudget.byCategory.TECH)}
                          </p>
                        </div>
                        <div className="glass-strong rounded-lg p-4 bg-gradient-to-br from-gray-50/80 to-gray-100/40 dark:from-gray-800/30 dark:to-gray-700/20 hover-lift">
                          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">Support</p>
                          <p className="text-xl font-bold text-gray-600 dark:text-gray-400">
                            {formatCurrencyCompact(program.isBudget.byCategory.SUPPORT)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Investible Structures & EPICs */}
                    {program.isBudget.investibleStructures.length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">
                          Supporting Investible Structures ({program.isBudget.investibleStructures.length})
                        </h4>
                        <div className="space-y-3">
                          {program.isBudget.investibleStructures.map(is => (
                            <div key={is.isId} className="glass-strong rounded-lg p-4 border border-white/30">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <p className="font-bold text-gray-900">{is.isName}</p>
                                  {getCategoryBadge(is.isCategory)}
                                </div>
                                <p className="text-base font-bold text-violet-600">
                                  {formatCurrencyCompact(is.totalBudget)}
                                </p>
                              </div>
                              <div className="space-y-1.5">
                                {is.epics.map(epic => (
                                  <div key={epic.epicId} className="glass rounded-lg py-2 px-3 hover:bg-white/60 transition-colors">
                                    <div className="flex items-center justify-between text-xs">
                                      <div className="flex items-center gap-2">
                                        <span className="font-bold text-gray-900">{epic.epicName}</span>
                                        <Badge variant={epic.priority === 'HIGH' ? 'danger' : epic.priority === 'MEDIUM' ? 'warning' : 'secondary'}>
                                          {epic.priority}
                                        </Badge>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <span className="text-gray-600 font-medium">{epic.requestedTeamWeeks} TW</span>
                                        <span className="font-bold text-violet-600">
                                          {formatCurrencyCompact(epic.budget)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {filteredPrograms.length === 0 && (
            <div className="glass-strong rounded-xl p-12 text-center">
              <p className="text-gray-600 font-medium">No programs match the selected filters</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinanceOperationalCostPage;
